import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { IVisibleLobby } from '../../../Models/LobbyModels';
import LobbyListItem from '../LobbyListItem/LobbyListItem';
import StartScreen, { SectionHeading } from '../StartScreen/StartScreen';
import {
  BUTTON_TEXT_CLASS_NAME,
  PRIMARY_BUTTON_CLASS_NAME,
  SECONDARY_BUTTON_CLASS_NAME,
} from '../StartScreen/styles';

interface ILobbyListProps {
  playerName: string;
  lobbyList: IVisibleLobby[];
  createAndJoinLobby: (lobbyName: string) => Promise<void>;
  goToLeaderboard: () => void;
  joinLobby: (lobbyId: string) => Promise<void>;
  refreshLobbies: () => Promise<void>;
  onBackButtonClick: () => void;
}

export default function LobbyList({
  playerName,
  lobbyList,
  createAndJoinLobby,
  goToLeaderboard,
  joinLobby,
  refreshLobbies,
  onBackButtonClick,
}: ILobbyListProps) {
  const [isJoining, setIsJoining] = useState(false);

  useEffect(() => {
    refreshLobbies();
  }, [refreshLobbies]);

  const join = (joinOrCreate: () => Promise<void>) => {
    setIsJoining(true);
    joinOrCreate().finally(() => setIsJoining(false));
  };

  const footer = (
    <>
      <button onClick={goToLeaderboard} className={SECONDARY_BUTTON_CLASS_NAME}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4zM7 6H4v2a3 3 0 0 0 3 3M17 6h3v2a3 3 0 0 1-3 3" />
        </svg>
        <span className={BUTTON_TEXT_CLASS_NAME}>Leaderboard</span>
      </button>
      <button
        onClick={() => join(() => createAndJoinLobby(`${playerName}'s Game`))}
        disabled={isJoining}
        className={classNames(PRIMARY_BUTTON_CLASS_NAME, 'flex-1')}
      >
        <span className={BUTTON_TEXT_CLASS_NAME}>New game</span>
      </button>
    </>
  );

  return (
    <StartScreen
      title="Games"
      backLabel="Change name"
      onBack={onBackButtonClick}
      footer={footer}
    >
      {lobbyList.length === 0 ? (
        <div className="flex flex-col items-center gap-[6px] px-[16px] pt-[72px] text-center">
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
            aria-hidden="true"
            className="mb-[8px] text-ink-muted"
          >
            <rect x="3" y="7" width="11" height="14" rx="2" />
            <path d="M8 7V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2h-4" />
          </svg>
          <span className="text-[16px] font-bold">No open games</span>
          <span className="text-[14px] font-medium text-ink-muted">
            Start a new one, and it shows up here for everyone else
          </span>
        </div>
      ) : (
        <section
          aria-labelledby="open-games"
          className="flex flex-col gap-[8px]"
        >
          <SectionHeading id="open-games" count={lobbyList.length}>
            Open games
          </SectionHeading>
          <ul className="flex flex-col gap-[8px]">
            {lobbyList.map((lobby) => (
              <LobbyListItem
                key={lobby.id}
                lobby={lobby}
                disabled={isJoining}
                joinLobby={(lobbyId) => join(() => joinLobby(lobbyId))}
              />
            ))}
          </ul>
        </section>
      )}
    </StartScreen>
  );
}
